import React, { useEffect, useState, useCallback } from 'react';
import {
    Card,
    Row,
    Col,
    Table,
    Button,
    message,
    Input,
    Select,
    Space,
    Tag,
    Spin,
    Empty,
    Statistic,
    Badge,
    Tooltip,
    Popconfirm,
    Modal,
} from 'antd';
import {
    SearchOutlined,
    FilterOutlined,
    EyeOutlined,
    CheckOutlined,
    ClockCircleOutlined,
    WarningOutlined,
    CloseCircleOutlined,
    FileTextOutlined,
    DashboardOutlined,
    ReloadOutlined,
    SafetyOutlined,
    CalendarOutlined,
    CheckCircleOutlined, // ✅ EKLENDI
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const { Search } = Input;
const { Option } = Select;

const CertificateView = () => {
    const { t } = useTranslation();
    const [dataSource, setDataSource] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterValidity, setFilterValidity] = useState('all');
    const [searchText, setSearchText] = useState('');
    const [selectedCertificate, setSelectedCertificate] = useState(null);
    const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);

    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("urlToken");

    // ✅ Fetch certificates
    const fetchCertificates = useCallback(async () => {
        try {
            setLoading(true);
            console.log('🔄 Fetching certificates...');

            // 1. Tüm certificate'ları getir
            const certificateResponse = await axios.get(`${apiUrl}/api/Certificates`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { pageSize: 1000 }
            });

            const certificateData = certificateResponse.data.data || certificateResponse.data.$values || [];
            console.log('✅ Certificates fetched:', certificateData.length);

            // 2. Her certificate için supplier bilgilerini getir
            const certificatesWithSuppliers = await Promise.all(
                certificateData.map(async (certificate) => {
                    try {
                        if (certificate.supplierId) {
                            const supplierResponse = await axios.get(
                                `${apiUrl}/api/Suppliers/${certificate.supplierId}`,
                                { headers: { Authorization: `Bearer ${token}` } }
                            );

                            const supplier = supplierResponse.data;

                            return {
                                ...certificate,
                                supplierName: supplier.supplierName,
                                supplierCode: supplier.supplierCode,
                                supplierCategory: supplier.category,
                                location: supplier.location,
                                qualityRating: supplier.qualityAuditOverallRating,
                            };
                        }
                        return null;
                    } catch (error) {
                        console.warn(`Failed to fetch supplier for certificate ${certificate.certificateId}`);
                        return null;
                    }
                })
            );

            const filteredCertificates = certificatesWithSuppliers.filter(cert => cert !== null);
            setDataSource(filteredCertificates);
            setFilteredData(filteredCertificates);
        } catch (error) {
            console.error("Error fetching certificates:", error);
            message.error(t('error_loading_data'));
        } finally {
            setLoading(false);
        }
    }, [apiUrl, token, t]); // ✅ Dependencies eklendi

    useEffect(() => {
        fetchCertificates();
    }, [fetchCertificates]); // ✅ fetchCertificates dependency olarak eklendi

    // ✅ Get validity status
    const getValidityStatus = useCallback((validityDate) => {
        const currentDate = moment();
        const validity = moment(validityDate);

        if (!validity.isValid()) return { status: 'unknown', color: 'default', text: t('unknown'), icon: <ClockCircleOutlined /> };

        const daysLeft = validity.diff(currentDate, 'days');

        if (daysLeft < 0) {
            return { status: 'expired', color: 'red', text: t('expired'), icon: <CloseCircleOutlined />, daysLeft };
        } else if (daysLeft <= 30) {
            return { status: 'expiring', color: 'orange', text: t('expiring_soon'), icon: <WarningOutlined />, daysLeft };
        } else {
            return { status: 'valid', color: 'green', text: t('valid'), icon: <CheckCircleOutlined />, daysLeft };
        }
    }, [t]); // ✅ Dependencies eklendi

    // ✅ Apply filters
    const applyFilters = useCallback((data = dataSource) => {
        let filtered = [...data];

        // Search filter
        if (searchText.trim() !== '') {
            filtered = filtered.filter((item) =>
                item.supplierName?.toLowerCase().includes(searchText.toLowerCase()) ||
                item.certificateName?.toLowerCase().includes(searchText.toLowerCase()) ||
                item.supplierCode?.toLowerCase().includes(searchText.toLowerCase())
            );
        }

        // Status filter
        if (filterStatus !== 'all') {
            filtered = filtered.filter((item) =>
                filterStatus === 'approved'
                    ? item.situation === '1'
                    : item.situation === '0'
            );
        }

        // Validity filter
        if (filterValidity !== 'all') {
            filtered = filtered.filter((item) => {
                const status = getValidityStatus(item.certificateValidityDate);
                return status.status === filterValidity;
            });
        }

        setFilteredData(filtered);
    }, [dataSource, searchText, filterStatus, filterValidity, getValidityStatus]); // ✅ Dependencies eklendi

    // ✅ Approve certificate
    const approveCertificate = async (certificateId) => {
        try {
            await axios.put(
                `${apiUrl}/api/Certificates/${certificateId}/approve`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            message.success(t('certificate_approved'));

            const updatedData = dataSource.map((cert) =>
                cert.certificateId === certificateId
                    ? { ...cert, situation: '1' }
                    : cert
            );
            setDataSource(updatedData);
            applyFilters(updatedData);
        } catch (error) {
            message.error(t('certificate_approve_error'));
            console.error('Approve Error:', error);
        }
    };

    // ✅ View certificate
    const handleViewCertificate = async (certificateId) => {
        try {
            const response = await axios.get(
                `${apiUrl}/api/Certificates/${certificateId}/sas`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const sasUrl = response.data.sasUrl;

            if (sasUrl) {
                window.open(sasUrl, "_blank");
            } else {
                message.error(t('sas_url_error'));
            }
        } catch (error) {
            message.error(t('certificate_fetch_error'));
            console.error('View Certificate Error:', error);
        }
    };

    // ✅ Show certificate details
    const showCertificateDetails = (certificate) => {
        setSelectedCertificate(certificate);
        setIsDetailModalVisible(true);
    };

    // ✅ Handle search
    const handleSearch = (value) => {
        setSearchText(value);
    };

    useEffect(() => {
        applyFilters();
    }, [applyFilters]); // ✅ applyFilters dependency olarak eklendi

    // ✅ Statistics
    const stats = {
        total: dataSource.length,
        approved: dataSource.filter(c => c.situation === '1').length,
        pending: dataSource.filter(c => c.situation === '0').length,
        expired: dataSource.filter(c => getValidityStatus(c.certificateValidityDate).status === 'expired').length,
        expiring: dataSource.filter(c => getValidityStatus(c.certificateValidityDate).status === 'expiring').length,
        valid: dataSource.filter(c => getValidityStatus(c.certificateValidityDate).status === 'valid').length,
    };

    // ✅ Table columns
    const columns = [
        {
            title: t('certificate_name'),
            dataIndex: 'certificateName',
            key: 'certificateName',
            width: 200,
            fixed: 'left',
            render: (text, record) => (
                <div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#262626' }}>
                        {text}
                    </div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                        ID: {record.certificateId}
                    </div>
                </div>
            ),
        },
        {
            title: t('supplier'),
            key: 'supplier',
            width: 200,
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#262626' }}>
                        {record.supplierName}
                    </div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                        {record.supplierCode}
                    </div>
                </div>
            ),
        },
        {
            title: t('category'),
            dataIndex: 'supplierCategory',
            key: 'supplierCategory',
            width: 120,
            render: (text) => <Tag color="blue">{text}</Tag>,
        },
        {
            title: t('validity_date'),
            dataIndex: 'certificateValidityDate',
            key: 'certificateValidityDate',
            width: 180,
            sorter: (a, b) => moment(a.certificateValidityDate).unix() - moment(b.certificateValidityDate).unix(),
            render: (text) => {
                const status = getValidityStatus(text);
                return (
                    <div>
                        <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                            <CalendarOutlined style={{ marginRight: '6px', color: '#1890ff' }} />
                            {moment(text).format('DD/MM/YYYY')}
                        </div>
                        <Tag icon={status.icon} color={status.color} style={{ fontSize: '12px' }}>
                            {status.text}
                            {status.daysLeft !== undefined && status.daysLeft >= 0 && (
                                <span> ({status.daysLeft} {t('days')})</span>
                            )}
                        </Tag>
                    </div>
                );
            },
        },
        {
            title: t('status'),
            dataIndex: 'situation',
            key: 'situation',
            width: 150,
            align: 'center',
            render: (text) => (
                <Tag
                    icon={text === '1' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                    color={text === '1' ? 'green' : 'orange'}
                    style={{ fontSize: '13px' }}
                >
                    {text === '1' ? t('approved') : t('waiting_for_approval')}
                </Tag>
            ),
        },
        {
            title: t('actions'),
            key: 'actions',
            width: 200,
            fixed: 'right',
            align: 'center',
            render: (_, record) => (
                <Space>
                    <Tooltip title={t('view_details')}>
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => showCertificateDetails(record)}
                        />
                    </Tooltip>
                    <Tooltip title={t('view_document')}>
                        <Button
                            type="text"
                            icon={<FileTextOutlined />}
                            onClick={() => handleViewCertificate(record.certificateId)}
                        />
                    </Tooltip>
                    {record.situation === '0' && (
                        <Popconfirm
                            title={t('approve_certificate_confirm')}
                            onConfirm={() => approveCertificate(record.certificateId)}
                            okText={t('yes')}
                            cancelText={t('no')}
                        >
                            <Tooltip title={t('approve')}>
                                <Button
                                    type="text"
                                    icon={<CheckOutlined />}
                                    style={{ color: '#52c41a' }}
                                />
                            </Tooltip>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
            {/* Header */}
            <div style={{
                background: '#fff',
                padding: '32px',
                borderRadius: '12px',
                marginBottom: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: '4px solid #1890ff'
            }}>
                <h1 style={{
                    fontSize: '32px',
                    fontWeight: '700',
                    color: '#262626',
                    margin: 0
                }}>
                    <DashboardOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
                    {t('certificate_management')}
                </h1>
                <p style={{ color: '#8c8c8c', margin: '8px 0 0 0', fontSize: '15px' }}>
                    {t('view_and_manage_supplier_certificates')}
                </p>
            </div>

            {/* Statistics Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} lg={8}>
                    <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <Statistic
                            title={t('total_certificates')}
                            value={stats.total}
                            prefix={<FileTextOutlined style={{ color: '#1890ff' }} />}
                            valueStyle={{ fontSize: '32px', fontWeight: '700', color: '#1890ff' }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                    <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <Statistic
                            title={t('approved')}
                            value={stats.approved}
                            prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                            valueStyle={{ fontSize: '32px', fontWeight: '700', color: '#52c41a' }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                    <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <Badge count={stats.pending} offset={[10, 0]}>
                            <Statistic
                                title={t('pending_approval')}
                                value={stats.pending}
                                prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
                                valueStyle={{ fontSize: '32px', fontWeight: '700', color: '#faad14' }}
                            />
                        </Badge>
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                    <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <Statistic
                            title={t('valid_certificates')}
                            value={stats.valid}
                            prefix={<SafetyOutlined style={{ color: '#52c41a' }} />}
                            valueStyle={{ fontSize: '32px', fontWeight: '700', color: '#52c41a' }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                    <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <Badge count={stats.expiring} offset={[10, 0]}>
                            <Statistic
                                title={t('expiring_soon')}
                                value={stats.expiring}
                                prefix={<WarningOutlined style={{ color: '#faad14' }} />}
                                valueStyle={{ fontSize: '32px', fontWeight: '700', color: '#faad14' }}
                            />
                        </Badge>
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                    <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <Statistic
                            title={t('expired')}
                            value={stats.expired}
                            prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                            valueStyle={{ fontSize: '32px', fontWeight: '700', color: '#ff4d4f' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Filters */}
            <Card style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={24} md={10}>
                        <Search
                            placeholder={t('search_certificate_or_supplier')}
                            allowClear
                            onSearch={handleSearch}
                            onChange={(e) => handleSearch(e.target.value)}
                            size="large"
                            prefix={<SearchOutlined />}
                        />
                    </Col>

                    <Col xs={24} sm={12} md={5}>
                        <Select
                            placeholder={t('filter_by_status')}
                            allowClear
                            onChange={(value) => setFilterStatus(value || 'all')}
                            style={{ width: '100%' }}
                            value={filterStatus}
                            size="large"
                            suffixIcon={<FilterOutlined />}
                        >
                            <Option value="all">{t('all_status')}</Option>
                            <Option value="approved">{t('approved')}</Option>
                            <Option value="waiting">{t('waiting_for_approval')}</Option>
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={5}>
                        <Select
                            placeholder={t('filter_by_validity')}
                            allowClear
                            onChange={(value) => setFilterValidity(value || 'all')}
                            style={{ width: '100%' }}
                            value={filterValidity}
                            size="large"
                            suffixIcon={<FilterOutlined />}
                        >
                            <Option value="all">{t('all_validity')}</Option>
                            <Option value="valid">{t('valid')}</Option>
                            <Option value="expiring">{t('expiring_soon')}</Option>
                            <Option value="expired">{t('expired')}</Option>
                        </Select>
                    </Col>

                    <Col xs={24} sm={24} md={4} style={{ textAlign: 'right' }}>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchCertificates}
                            loading={loading}
                            size="large"
                            style={{ width: '100%' }}
                        >
                            {t('refresh')}
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Table */}
            <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '100px 0' }}>
                        <Spin size="large" tip={t('loading')} />
                    </div>
                ) : filteredData.length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t('no_certificates_found')}
                    />
                ) : (
                    <Table
                        columns={columns}
                        dataSource={filteredData}
                        rowKey="certificateId"
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total) => `${t('total')} ${total} ${t('certificates')}`,
                        }}
                        scroll={{ x: 1200 }}
                        size="middle"
                    />
                )}
            </Card>

            {/* Certificate Detail Modal */}
            <Modal
                title={
                    <span style={{ fontSize: '18px', fontWeight: '600' }}>
                        <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                        {t('certificate_details')}
                    </span>
                }
                visible={isDetailModalVisible}
                onCancel={() => {
                    setIsDetailModalVisible(false);
                    setSelectedCertificate(null);
                }}
                footer={[
                    <Button key="close" onClick={() => setIsDetailModalVisible(false)}>
                        {t('close')}
                    </Button>,
                    <Button
                        key="view"
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewCertificate(selectedCertificate?.certificateId)}
                    >
                        {t('view_document')}
                    </Button>,
                ]}
                width={700}
            >
                {selectedCertificate && (
                    <div>
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Card size="small" style={{ background: '#f0f2f5' }}>
                                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                        <div>
                                            <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>
                                                {t('certificate_name')}
                                            </div>
                                            <div style={{ fontSize: '16px', fontWeight: '600' }}>
                                                {selectedCertificate.certificateName}
                                            </div>
                                        </div>

                                        <div>
                                            <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>
                                                {t('supplier')}
                                            </div>
                                            <div style={{ fontSize: '16px', fontWeight: '600' }}>
                                                {selectedCertificate.supplierName}
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#8c8c8c' }}>
                                                {selectedCertificate.supplierCode}
                                            </div>
                                        </div>

                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>
                                                    {t('category')}
                                                </div>
                                                <Tag color="blue">{selectedCertificate.supplierCategory}</Tag>
                                            </Col>
                                            <Col span={12}>
                                                <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>
                                                    {t('location')}
                                                </div>
                                                <div style={{ fontSize: '14px' }}>{selectedCertificate.location}</div>
                                            </Col>
                                        </Row>

                                        <div>
                                            <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>
                                                {t('validity_date')}
                                            </div>
                                            <div style={{ fontSize: '16px', fontWeight: '600' }}>
                                                {moment(selectedCertificate.certificateValidityDate).format('DD MMMM YYYY')}
                                            </div>
                                            {(() => {
                                                const status = getValidityStatus(selectedCertificate.certificateValidityDate);
                                                return (
                                                    <Tag icon={status.icon} color={status.color} style={{ marginTop: '8px' }}>
                                                        {status.text}
                                                        {status.daysLeft !== undefined && status.daysLeft >= 0 && (
                                                            <span> - {status.daysLeft} {t('days_remaining')}</span>
                                                        )}
                                                    </Tag>
                                                );
                                            })()}
                                        </div>

                                        <div>
                                            <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>
                                                {t('approval_status')}
                                            </div>
                                            <Tag
                                                icon={selectedCertificate.situation === '1' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                                                color={selectedCertificate.situation === '1' ? 'green' : 'orange'}
                                                style={{ fontSize: '14px' }}
                                            >
                                                {selectedCertificate.situation === '1' ? t('approved') : t('waiting_for_approval')}
                                            </Tag>
                                        </div>

                                        {selectedCertificate.qualityRating && (
                                            <div>
                                                <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>
                                                    {t('quality_rating')}
                                                </div>
                                                <div style={{ fontSize: '14px' }}>{selectedCertificate.qualityRating}</div>
                                            </div>
                                        )}
                                    </Space>
                                </Card>
                            </Col>
                        </Row>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default CertificateView;
